import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Send, Smartphone } from 'lucide-react';
import { useSendNotification, getUserDeviceTokens } from '@/services/api/endpoints/UserService';
import type { User } from '@/types';

interface SendNotificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function SendNotificationModal({ open, onOpenChange, user }: SendNotificationModalProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deviceCount, setDeviceCount] = useState<number | null>(null);
  const sendMutation = useSendNotification();

  useEffect(() => {
    if (open && user) {
      setTitle('');
      setBody('');
      setDeviceCount(null);
      getUserDeviceTokens(user.id).then(res => setDeviceCount(res.count)).catch(() => setDeviceCount(0));
    }
  }, [open, user]);

  const handleSend = async () => {
    if (!user || !title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }

    try {
      const result = await sendMutation.mutateAsync({
        userId: user.id,
        title: title.trim(),
        body: body.trim(),
      });

      if (result.success) {
        toast.success(`Notification sent to ${result.successCount} device(s)`);
        onOpenChange(false);
      } else {
        toast.error(result.message || 'Failed to send notification');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send notification');
    }
  };

  if (!user) return null;

  const userName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Send Push Notification
          </DialogTitle>
          <DialogDescription>
            Send a push notification to {userName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* User Info */}
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border">
            <p className="text-sm font-medium">{userName}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
            <div className="flex items-center gap-1 mt-1">
              <Smartphone className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {deviceCount === null
                  ? 'Loading devices...'
                  : deviceCount === 0
                    ? 'No devices registered'
                    : `${deviceCount} device${deviceCount === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
            />
          </div>

          <div className="space-y-2">
            <Label>Message</Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification message"
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background resize-y"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!title.trim() || !body.trim() || sendMutation.isPending || deviceCount === 0}
          >
            {sendMutation.isPending ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
